import { Input as NInput, type InputProps as Props } from '@heroui/react';
import debounce from 'lodash.debounce';
import { useEffect, useMemo, useState } from 'react';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import { isEmpty } from '@/utils/object';

export const Input = (props: Props) => {
  return (
    <NInput
      // classNames={{ input: "text-default-800 placeholder:text-default-400" }}
      radius='sm'
      {...props}
    />
  );
};

export const SearchInput = ({ value, onValueChange, ...props }: Props) => {
  const [inputValue, setInputValue] = useState(value ?? '');

  useEffect(() => {
    if (!value) {
      setInputValue('');
      return;
    }
  }, [value]);

  const debouncedOnValueChange = useMemo(() => debounce(onValueChange ?? (() => {}), 500), [onValueChange]);

  useEffect(() => {
    return () => {
      debouncedOnValueChange.cancel();
    };
  }, [debouncedOnValueChange]);

  // Handle input change
  const handleChange = (val: string) => {
    setInputValue(val);
    debouncedOnValueChange(val);
  };

  return <Input {...props} value={inputValue} onValueChange={handleChange} />;
};

export interface FormInputProps<T extends FieldValues> extends Props {
  name: Path<T>;
  control: Control<T, unknown>;
  transform?: (val: string) => PathValue<T, Path<T>>;
  defaultValue?: PathValue<T, Path<T>>;
  shouldUnregister?: boolean;
}

export const FormInput = <T extends FieldValues>({
  control,
  transform,
  name,
  defaultValue,
  shouldUnregister = undefined,
  ...rest
}: FormInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      // shouldUnregister
      render={({ field: { onChange, onBlur, value }, fieldState: { error, invalid, isDirty } }) => {
        const onHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const val = event.target.value;
          if (transform && !isEmpty(val)) {
            const newVal = transform(val);
            onChange(newVal);

            return;
          }
          onChange(event);
        };
        return (
          <Input
            {...rest}
            value={value ?? defaultValue}
            onBlur={onBlur}
            onChange={onHandleChange}
            // isInvalid={true}
            isInvalid={isDirty && invalid}
            errorMessage={error?.message}
          />
        );
      }}
    />
  );
};
